<template>
  <div class="tax-rate-manage-page">
    <!-- 顶部导航 -->
    <header class="page-header">
      <div class="back-btn" @click="$router.back()">{{ $t('admin.TaxRateManage.line5_text') }}</div>
      <h1>{{ $t('admin.TaxRateManage.line6_text') }}</h1>
      <button class="btn btn-primary btn-add" @click="openCreateDialog">
        + 新增税率
      </button>
    </header>

    <div class="page-content">
      <!-- 税率列表 -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner">⏳</div>
        <p>{{ $t('admin.TaxRateManage.line16_text') }}</p>
      </div>

      <div v-else-if="taxRates.length === 0" class="empty-state">
        <div class="empty-icon">📊</div>
        <p>{{ $t('admin.TaxRateManage.line21_text') }}</p>
        <button class="btn btn-primary" @click="openCreateDialog">
          + 添加第一个税率
        </button>
      </div>

      <div v-else class="tax-rate-list">
        <div 
          v-for="tax in taxRates" 
          :key="tax.id" 
          class="tax-rate-card"
          :class="{ disabled: !tax.enabled, default: tax.isDefault }"
        >
          <div class="tax-header">
            <div class="tax-info">
              <span class="tax-name">{{ tax.name }}</span>
              <span v-if="tax.isDefault" class="badge-default">{{ $t('admin.TaxRateManage.line37_text') }}</span>
              <span v-if="!tax.enabled" class="badge-disabled">{{ $t('admin.TaxRateManage.line38_text') }}</span>
            </div>
            <div class="tax-actions">
              <button class="btn btn-sm btn-edit" @click="openEditDialog(tax)">
                ✏️ 编辑
              </button>
              <button class="btn btn-sm btn-delete" @click="handleDelete(tax)">
                🗑️ 删除
              </button>
            </div>
          </div>
          <div class="tax-body">
            <div class="tax-value">
              <span class="rate-label">{{ $t('admin.TaxRateManage.line51_text') }}</span>
              <span class="rate-value">{{ (tax.rate * 100).toFixed(1) }}%</span>
            </div>
            <div class="tax-status">
              <span class="status-label">{{ $t('admin.TaxRateManage.line55_text') }}</span>
              <span class="status-badge" :class="tax.enabled ? 'enabled' : 'disabled'">
                {{ tax.enabled ? '✅ 启用 / Enabled' : '⏸️ 停用 / Disabled' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showDialog" class="dialog-overlay" @click.self="closeDialog">
      <div class="dialog">
        <div class="dialog-header">
          <h3>{{ isEditing ? '编辑税率 / Edit Tax Rate' : '新增税率 / Add Tax Rate' }}</h3>
          <button class="dialog-close" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
          <div class="form-group">
            <label class="form-label">{{ $t('admin.TaxRateManage.line74_text') }} <span class="required">*</span></label>
            <input 
              v-model="form.name" 
              type="text" 
              class="form-input" 
              :placeholder="$t('admin.TaxRateManage.line79_placeholder')"
            />
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('admin.TaxRateManage.line83_text') }} <span class="required">*</span></label>
            <input 
              v-model.number="form.ratePercent" 
              type="number" 
              class="form-input" 
              :placeholder="$t('admin.TaxRateManage.line88_placeholder')"
              step="0.1"
              min="0"
              max="100"
            />
            <div class="form-hint">{{ $t('admin.TaxRateManage.line93_text') }}</div>
          </div>
          <div class="form-group form-checkbox">
            <label>
              <input type="checkbox" v-model="form.enabled" />
              <span>{{ $t('admin.TaxRateManage.line98_text') }}</span>
            </label>
          </div>
          <div class="form-group form-checkbox">
            <label>
              <input type="checkbox" v-model="form.isDefault" />
              <span>{{ $t('admin.TaxRateManage.line104_text') }}</span>
            </label>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn btn-secondary" @click="closeDialog">{{ $t('admin.TaxRateManage.line109_text') }}</button>
          <button class="btn btn-primary" @click="handleSave" :disabled="saving">
            {{ saving ? '保存中...' : '保存 / Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="showToast" class="toast">{{ toastMessage }}</div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'TaxRateManage',
  
  data() {
    return {
      taxRates: [],
      loading: false,
      showDialog: false,
      isEditing: false,
      saving: false,
      showToast: false,
      toastMessage: '',
      form: {
        id: null,
        name: '',
        ratePercent: null,
        enabled: true,
        isDefault: false
      }
    };
  },

  created() {
    this.loadTaxRates();
  },

  methods: {
    async loadTaxRates() {
      this.loading = true;
      try {
        const response = await api.get('/admin/tax-rates');
        this.taxRates = Array.isArray(response) ? response : (response.data || []);
        console.log('✅ 税率列表加载成功:', this.taxRates.length, '条');
      } catch (error) {
        console.error('❌ 加载税率失败:', error);
        this.showToastMsg('加载失败 / Failed to load');
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.form = {
        id: null,
        name: '',
        ratePercent: null,
        enabled: true,
        isDefault: false
      };
      this.showDialog = true;
    },

    openEditDialog(tax) {
      this.isEditing = true;
      this.form = {
        id: tax.id,
        name: tax.name,
        ratePercent: tax.rate * 100,
        enabled: tax.enabled,
        isDefault: tax.isDefault
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async handleSave() {
      if (!this.form.name || !this.form.name.trim()) {
        this.showToastMsg('请输入税率名称 / Please enter name');
        return;
      }
      if (this.form.ratePercent === null || this.form.ratePercent < 0) {
        this.showToastMsg('请输入有效税率 / Please enter valid rate');
        return;
      }

      this.saving = true;
      try {
        const dto = {
          name: this.form.name.trim(),
          rate: this.form.ratePercent / 100,
          enabled: this.form.enabled,
          isDefault: this.form.isDefault
        };

        if (this.isEditing) {
          await api.put(`/admin/tax-rates/${this.form.id}`, dto);
          this.showToastMsg('✅ 税率已更新 / Tax rate updated');
        } else {
          await api.post('/admin/tax-rates', dto);
          this.showToastMsg('✅ 税率已添加 / Tax rate added');
        }

        this.closeDialog();
        await this.loadTaxRates();
      } catch (error) {
        console.error('❌ 保存失败:', error);
        this.showToastMsg('保存失败 / Save failed: ' + error.message);
      } finally {
        this.saving = false;
      }
    },

    async handleDelete(tax) {
      const confirmed = confirm(
        `确定删除税率 "${tax.name}" 吗？\nAre you sure to delete "${tax.name}"?`
      );
      if (!confirmed) return;

      try {
        await api.delete(`/admin/tax-rates/${tax.id}`);
        this.showToastMsg('✅ 税率已删除 / Tax rate deleted');
        await this.loadTaxRates();
      } catch (error) {
        console.error('❌ 删除失败:', error);
        this.showToastMsg('删除失败 / Delete failed: ' + error.message);
      }
    },

    showToastMsg(msg) {
      this.toastMessage = msg;
      this.showToast = true;
      setTimeout(() => { this.showToast = false; }, 2500);
    }
  }
};
</script>

<style scoped>
.tax-rate-manage-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.page-header {
  background: white;
  padding: calc(16px + env(safe-area-inset-top, 0px)) 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  font-size: 16px;
  color: #667eea;
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
}

.page-header h1 {
  font-size: 18px;
  color: #333;
  margin: 0;
  flex: 1;
}

.page-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.loading-spinner {
  font-size: 48px;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state p {
  color: #6b7280;
  margin-bottom: 20px;
}

.tax-rate-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.tax-rate-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid #10b981;
  transition: all 0.2s;
}

.tax-rate-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}

.tax-rate-card.disabled {
  border-left-color: #9ca3af;
  opacity: 0.7;
}

.tax-rate-card.default {
  border-left-color: #667eea;
}

.tax-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.tax-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.tax-name {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.badge-default {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.badge-disabled {
  background: #e5e7eb;
  color: #6b7280;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.tax-actions {
  display: flex;
  gap: 8px;
}

.tax-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tax-value {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rate-label {
  font-size: 14px;
  color: #6b7280;
}

.rate-value {
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
}

.tax-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-label {
  font-size: 13px;
  color: #6b7280;
}

.status-badge {
  font-size: 13px;
  padding: 4px 12px;
  border-radius: 8px;
  
  &.enabled {
    background: #d1fae5;
    color: #065f46;
  }
  
  &.disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
}

/* 弹窗样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.dialog {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.dialog-header h3 {
  font-size: 18px;
  color: #1f2937;
  margin: 0;
}

.dialog-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.dialog-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.form-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-checkbox label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.form-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-add {
  white-space: nowrap;
  padding: 8px 16px;
  font-size: 14px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-edit {
  background: #e0f2fe;
  color: #0369a1;
}

.btn-delete {
  background: #fee2e2;
  color: #991b1b;
}

.toast {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
</style>
